import { useCallback, useEffect, useState } from 'react'
import { AppShell, type PageId } from './components/AppShell'
import { ParentHoldButton } from './components/ParentHoldButton'
import { concepts, lessonById } from './data/foundations'
import { currentConcept } from './engine/learningJourney'
import type { PracticeSession } from './engine/practiceSession'
import { audioManager } from './services/AudioManager'
import { isPackagedApp } from './services/runtimePlatform'
import { useWebMcp } from './hooks/useWebMcp'
import { AudioQaPage } from './pages/AudioQaPage'
import { CurriculumQaPage } from './pages/CurriculumQaPage'
import { HomePage } from './pages/HomePage'
import { LearningMapPage } from './pages/LearningMapPage'
import { LessonPage } from './pages/LessonPage'
import { ParentDashboard } from './pages/ParentDashboard'
import { WordLibraryPage } from './pages/WordLibraryPage'
import { useProgress } from './store/ProgressContext'
import type { Lesson } from './types/content'

interface Route { page: PageId; lessonId?: string; session?: PracticeSession; run?: number }

const parseLocation = (): Route => {
  const legacy = window.location.hash.replace(/^#\/?/, '')
  const path = legacy || window.location.pathname.replace(/^\/+|\/+$/g, '')
  if (path.startsWith('lesson/')) {
    const params = new URLSearchParams(window.location.search)
    const requestedMode = params.get('mode')
    const mode = requestedMode === 'review' || requestedMode === 'independent' || requestedMode === 'transfer' ? requestedMode : 'lesson'
    let lessonId = path.slice('lesson/'.length)
    try { lessonId = decodeURIComponent(lessonId) } catch { /* Invalid paths show the safe not-found screen. */ }
    return { page: 'lesson', lessonId, session: { mode, wordId: mode === 'lesson' ? undefined : params.get('word') ?? undefined } }
  }
  if (path === 'map' || path === 'words' || path === 'parent') return { page: path }
  if (path === 'dev/audio') return { page: 'dev-audio' }
  if (path === 'dev/curriculum') return { page: 'dev-curriculum' }
  return { page: 'home' }
}

const pathFor = (page: PageId, lessonId?: string) => page === 'home' ? '/' : page === 'lesson' && lessonId ? `/lesson/${encodeURIComponent(lessonId)}` : page === 'dev-audio' ? '/dev/audio' : page === 'dev-curriculum' ? '/dev/curriculum' : `/${page}`

export default function App() {
  const { progress, saveError } = useProgress()
  const [parentAccess, setParentAccess] = useState(false)
  const [route, setRoute] = useState<Route>(parseLocation)
  const [missingAudio, setMissingAudio] = useState<string | null>(null)
  const lesson = route.lessonId ? lessonById.get(route.lessonId) : undefined

  const go = useCallback((page: PageId, lessonId?: string, session?: PracticeSession) => {
    const params = new URLSearchParams()
    if (page === 'lesson' && session && session.mode !== 'lesson') {
      params.set('mode', session.mode)
      if (session.wordId) params.set('word', session.wordId)
    }
    window.history.pushState({}, '', `${pathFor(page, lessonId)}${params.size ? `?${params}` : ''}`)
    setRoute((previous) => ({ page, lessonId, session, run: (previous.run ?? 0) + 1 }))
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  useEffect(() => {
    audioManager.stop()
    setMissingAudio(null)
    document.getElementById('main-content')?.focus({ preventScroll: true })
    if (route.page !== 'parent') setParentAccess(false)
    return () => audioManager.stop()
  }, [route.page, route.lessonId, route.session, route.run])

  useEffect(() => {
    const onLocation = () => setRoute(parseLocation())
    window.addEventListener('popstate', onLocation)
    window.addEventListener('hashchange', onLocation)
    return () => { window.removeEventListener('popstate', onLocation); window.removeEventListener('hashchange', onLocation) }
  }, [])

  useEffect(() => {
    const onHidden = () => { if (document.visibilityState === 'hidden') audioManager.stop() }
    const onPageHide = () => audioManager.stop()
    document.addEventListener('visibilitychange', onHidden)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      document.removeEventListener('visibilitychange', onHidden)
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [])

  useEffect(() => {
    let timer = 0
    const onMissing = (event: Event) => {
      const id = (event as CustomEvent<{ assetId: string }>).detail.assetId
      setMissingAudio(id)
      window.clearTimeout(timer)
      timer = window.setTimeout(() => setMissingAudio(null), 2600)
    }
    window.addEventListener('phonics-audio-missing', onMissing)
    return () => { window.removeEventListener('phonics-audio-missing', onMissing); window.clearTimeout(timer) }
  }, [])

  const startLessonById = useCallback((lessonId: string) => go('lesson', lessonId), [go])
  useWebMcp({ progress, startLesson: startLessonById })
  const start = (item: Lesson, session?: PracticeSession) => go('lesson', item.id, session)
  const isDev = route.page === 'dev-audio' || route.page === 'dev-curriculum'
  const [qaAccess, setQaAccess] = useState(false)
  const qaLocked = isDev && isPackagedApp() && !qaAccess
  const lessonUnlocked = lesson && (concepts.find((item) => item.id === lesson.conceptId)?.order ?? Infinity) <= currentConcept(progress).order

  return (
    <AppShell page={route.page} stars={progress.stars} onNavigate={(page) => { if (page === 'parent') setParentAccess(true); go(page) }} hideNavigation={route.page === 'lesson' || route.page === 'parent' || isDev}>
      {saveError && <div role="alert" className="mb-5 rounded-2xl bg-amber-50 p-4 text-sm text-amber-950">进度暂时无法保存，请请家长检查本机储存空间。</div>}
      {route.page === 'home' && <HomePage onStart={start} onOpenMap={() => go('map')} />}
      {route.page === 'map' && <LearningMapPage onStart={start} />}
      {route.page === 'words' && <WordLibraryPage onLearn={() => go('map')} />}
      {route.page === 'parent' && (parentAccess ? <ParentDashboard onBack={() => go('home')} /> : <section className="surface mx-auto mt-8 max-w-md text-center"><h1 className="text-2xl font-black">请家长来看看</h1><p className="mt-3 text-sm text-garden-700">按住下面的按钮两秒进入。</p><ParentHoldButton onOpen={() => setParentAccess(true)} className="mx-auto mt-6 flex min-h-16 w-full flex-col items-center justify-center gap-1 rounded-2xl bg-garden-700 font-bold text-white">进入家长页面</ParentHoldButton><button className="quiet-button mt-4" onClick={() => go('home')}>回到小花园</button></section>)}
      {qaLocked && <section className="surface mx-auto max-w-md text-center"><h1 className="text-2xl font-black">成人验收区域</h1><p className="mt-3 text-sm leading-6">这里的试听结果不会自动认证音素。请家长按住按钮两秒进入。</p><ParentHoldButton onOpen={() => setQaAccess(true)} className="quiet-button mt-5 w-full !bg-garden-100">进入验收</ParentHoldButton><button className="quiet-button mt-4" onClick={() => go('home')}>回到小花园</button></section>}
      {route.page === 'dev-audio' && !qaLocked && <AudioQaPage />}
      {route.page === 'dev-curriculum' && !qaLocked && <CurriculumQaPage />}
      {route.page === 'lesson' && lesson && lessonUnlocked && <LessonPage key={`${lesson.id}:${route.session?.mode ?? 'lesson'}:${route.session?.wordId ?? ''}:${route.run ?? 0}`} lesson={lesson} session={route.session} onExit={() => go('map')} onNext={start} />}
      {route.page === 'lesson' && lesson && !lessonUnlocked && <section className="surface text-center"><h1 className="text-2xl font-bold">先完成前面的小脚印</h1><button onClick={() => go('map')} className="quiet-button mt-5 !bg-garden-100">回到花园路线</button></section>}
      {route.page === 'lesson' && !lesson && <div className="mx-auto max-w-lg rounded-4xl bg-white p-8 text-center shadow-soft"><h1 className="text-2xl font-black">这节课找不到了</h1><button className="mt-5 min-h-14 rounded-2xl bg-garden-700 px-6 font-extrabold text-white" onClick={() => go('map')}>回到学习路线</button></div>}
      {missingAudio && <div className="fixed bottom-24 left-1/2 z-50 w-[min(90vw,420px)] -translate-x-1/2 rounded-2xl bg-ink px-5 py-3 text-center text-sm font-bold text-white shadow-soft" role="status">{isDev ? `音频不可用：${missingAudio}` : '暂时听不到这个声音，请请家长帮忙。'}<br /><span className="text-garden-200">{isDev ? '不会用字母名代替音素。' : '也可以先回花园，进度会保留。'}</span></div>}
    </AppShell>
  )
}
